import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useFormatCurrency } from "utils/customHooks/useFormatCurrency";

import { Button } from "@krea/shared/design-system/Button";
import { AppModal } from "@krea/shared/design-system/modal";

import {
  formatLenderResponseFormData,
  getFormValuesFromLenderResponse,
} from "@/components/lender-application-details/bids/bidHelpers";
import { BidsForm } from "@/components/lender-application-details/bids/LenderResponseForm";
import { BidTypeEnum } from "@/features/common/enums";
import type { ILenderApplicationExtended } from "@/features/lender-applications/types/lender-application";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { useMarkAsPaidOut } from "@/features/lender-responses/hooks/data/use-mark-as-paid-out.mutation";
import type { ILenderResponse } from "@/features/lender-responses/types/lender-response";
import { type BidPostData, bidValidationSchema } from "@/validations";

interface IProps {
  setOpen: (isOpen: boolean) => void;
  lenderResponse: ILenderResponse;
  lenderApplication: ILenderApplicationExtended;
}

export const MarkPaidOutModal = ({
  setOpen,
  lenderResponse,
  lenderApplication,
}: IProps) => {
  const { loanApplication } = lenderApplication;
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();

  const isInterestRatePerYearActivated =
    lenderCompany?.isFeatureInterestRatePerYearActivated;

  const [formattedCurrencyValue] = useFormatCurrency({
    currency: loanApplication.currency,
  });

  const [defaultValues] = useState<BidPostData>(
    getFormValuesFromLenderResponse(
      lenderResponse,
      isInterestRatePerYearActivated,
    ),
  );

  const methods = useForm<BidPostData>({
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      bidValidationSchema({
        currency: loanApplication.currency,
        estimatedInvoiceAmount: 0,
        formatWithCurrency: (value) =>
          formattedCurrencyValue(value, loanApplication.currency),
        totalLoanAmount: lenderResponse.bids.reduce(
          (sum, bid) =>
            sum +
            (bid.type === BidTypeEnum.FACTORING
              ? bid.purchaseLimit
              : bid.amount),
          0,
        ),
      }),
    ),
    defaultValues: { ...defaultValues },
    shouldUnregister: false,
  });

  const { mutate: markAsPaidOut, isPending: isMarkAsPaidOutPending } =
    useMarkAsPaidOut();

  const onCloseModal = () => {
    setOpen(false);
  };

  const onSubmit = (data: BidPostData) => {
    const formattedData: BidPostData = formatLenderResponseFormData(
      data,
      "TO_MONTHLY",
      isInterestRatePerYearActivated,
    );
    markAsPaidOut(
      {
        lenderCompanyId: Number(lenderCompany?.id),
        lenderResponseId: lenderResponse.id,
        input: formattedData,
      },
      {
        onSuccess: () => {
          onCloseModal();
        },
      },
    );
  };

  return (
    <AppModal
      open={true}
      setOpen={setOpen}
      onClose={onCloseModal}
      title={`Mark as paid out - application #${lenderApplication.id}`}
      footer={
        <>
          <Button variant="secondary" onClick={onCloseModal}>
            Cancel
          </Button>

          <Button
            variant={"success"}
            onClick={() => methods.handleSubmit(onSubmit)()}
            disabled={isMarkAsPaidOutPending || !methods.formState.isValid}
          >
            Mark as paid out
          </Button>
        </>
      }
    >
      <h6 className="mb-5">
        Are you sure you want to mark application #{lenderApplication.id} as
        paid out?
      </h6>

      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
          id="markAsPaidOutForm"
        >
          <BidsForm
            lenderApplication={lenderApplication}
            isEditing={true}
            isInterestRatePerYearActivated={
              isInterestRatePerYearActivated ?? false
            }
          />
        </form>
      </FormProvider>
    </AppModal>
  );
};
